import '../scss/components/_owneroccupancy.scss'
import React, { useState, useEffect } from 'react'
import { get_occupancy_status } from '../apis/services/pgApi'
import TenantCheckoutModal from './TenantCheckoutModal'

const STATUS = {
  OCCUPIED: { label: 'Occupied', color: '#22C55E', bg: '#F0FDF4', border: '#86EFAC', text: '#166534' },
  NOTICE: { label: 'Notice Period', color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' },
  VACANT: { label: 'Vacant', color: '#EF4444', bg: '#FFF1F2', border: '#FECACA', text: '#991B1B' },
}

// Status filter options (without 'All')
const STATUS_FILTERS = [
  { key: 'occupied', label: 'Occupied', bg: '#22C55E' },
  { key: 'notice', label: 'Notice Period', bg: '#F59E0B' },
  { key: 'vacant', label: 'Vacant', bg: '#EF4444' },
]

const OwnerOccupancy = ({ selectedPG, selectedPGId, pgDetails, onVacantClick }) => {
  const [floorFilter, setFloorFilter] = useState('all')
  const [selectedStatuses, setSelectedStatuses] = useState([]) // Array for multi-select status
  const [search, setSearch] = useState('')
  const [roomsData, setRoomsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    totalRooms: 0,
    totalBeds: 0,
    occupiedBeds: 0,
    noticeBeds: 0,
    vacantBeds: 0
  })

  // State for checkout modal
  const [selectedBedForCheckout, setSelectedBedForCheckout] = useState(null)
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)

  // Define floor order for sorting
  const floorOrder = {
    'Ground Floor': 0,
    '1st Floor': 1,
    '2nd Floor': 2,
    '3rd Floor': 3,
    '4th Floor': 4,
    '5th Floor': 5
  }

  useEffect(() => {
    if (selectedPGId) {
      fetchOccupancyData()
    }
  }, [selectedPGId])

  const fetchOccupancyData = async () => {
    try {
      setLoading(true)
      setError(null)

      const pgid = selectedPGId

      if (!pgid) {
        throw new Error('PG ID not found')
      }

      const response = await get_occupancy_status(pgid)

      console.log("API Response:", response)

      // Handle different response structures
      let bedsData = []
      let summaryData = []
      let totalsData = []

      if (Array.isArray(response)) {
        if (response.length === 3) {
          bedsData = response[0] || []
          summaryData = response[1] || []
          totalsData = response[2] || []
        } else if (response.length === 1) {
          bedsData = response[0] || []
        } else {
          bedsData = response || []
        }
      } else if (response && response.data) {
        bedsData = response.data || []
      } else {
        bedsData = response || []
      }

      console.log("Beds Data:", bedsData)

      // Transform beds data into rooms structure
      const { rooms, totalRooms, totalBeds, occupiedBeds, noticeBeds, vacantBeds } = transformToRooms(bedsData)

      console.log("Transformed Rooms:", rooms)

      setRoomsData(rooms)

      setStats({
        totalRooms,
        totalBeds,
        occupiedBeds,
        noticeBeds,
        vacantBeds
      })

    } catch (err) {
      setError(err.message || 'Failed to fetch occupancy data')
      console.error('Error fetching occupancy data:', err)
    } finally {
      setLoading(false)
    }
  }

  const transformToRooms = (bedsData) => {
    const roomsMap = new Map()
    let totalBeds = 0
    let occupiedBeds = 0
    let noticeBeds = 0
    let vacantBeds = 0

    if (!bedsData || bedsData.length === 0) {
      console.log("No beds data")
      return { rooms: [], totalRooms: 0, totalBeds: 0, occupiedBeds: 0, noticeBeds: 0, vacantBeds: 0 }
    }

    bedsData.forEach(bed => {
      totalBeds++

      const roomId = bed.RoomID
      const roomNumber = bed.RoomNumber
      const floor = bed.Floor || "Unknown"
      const roomType = bed.RoomType || "Standard"

      console.log(`Processing bed - Room: ${roomNumber}, Floor: ${floor}`)

      if (!roomsMap.has(roomId)) {
        roomsMap.set(roomId, {
          id: roomNumber,
          roomId: roomId,
          floor: floor,
          roomType: roomType,
          beds: []
        })
      }

      const room = roomsMap.get(roomId)
      const bedStatus = (bed.OccupancyStatus || '').toUpperCase()
      let status = 'vacant'

      if (bedStatus === 'OCCUPIED') {
        status = 'occupied'
        occupiedBeds++
      } else if (bedStatus === 'NOTICE') {
        status = 'notice'
        noticeBeds++
      } else {
        vacantBeds++
      }

      room.beds.push({
        bedId: bed.BedID,
        bedNumber: bed.BedNumber,
        name: bed.TenantName || '',
        status: status,
        checkInDate: bed.CheckInDate,
        rent: bed.Rent,
        roomNumber: roomNumber,
        roomId: roomId,
        floor: floor,
        roomType: roomType,

        TenantName: bed.TenantName,
        AdmissionID: bed.AdmissionID,
        TenantID: bed.TenantID,
        OccupancyStatus: bed.OccupancyStatus,
        BedID: bed.BedID
      })
    })

    // Sort rooms by floor order and then by room number
    const sortedRooms = Array.from(roomsMap.values()).sort((a, b) => {
      const aFloorOrder = floorOrder[a.floor] !== undefined ? floorOrder[a.floor] : 999
      const bFloorOrder = floorOrder[b.floor] !== undefined ? floorOrder[b.floor] : 999

      if (aFloorOrder !== bFloorOrder) {
        return aFloorOrder - bFloorOrder
      }

      const aNum = parseInt(a.id)
      const bNum = parseInt(b.id)
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum
      }
      return String(a.id).localeCompare(String(b.id))
    })

    console.log("Sorted Rooms with floors:", sortedRooms.map(r => ({ id: r.id, floor: r.floor })))

    return {
      rooms: sortedRooms,
      totalRooms: roomsMap.size,
      totalBeds,
      occupiedBeds,
      noticeBeds,
      vacantBeds
    }
  }

  // Get unique floors from data with proper ordering
  const getAvailableFloors = () => {
    const floors = [...new Set(roomsData.map(room => room.floor))]
    console.log("Available floors:", floors)

    return floors.sort((a, b) => {
      const aOrder = floorOrder[a] !== undefined ? floorOrder[a] : 999
      const bOrder = floorOrder[b] !== undefined ? floorOrder[b] : 999
      return aOrder - bOrder
    })
  }

  // Handle status filter toggle (multi-select)
  const toggleStatusFilter = (statusKey) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusKey)) {
        // Remove if already selected
        return prev.filter(s => s !== statusKey)
      } else {
        // Add if not selected
        return [...prev, statusKey]
      }
    })
  }

  const handleVacantClick = (bed) => {
    if (onVacantClick) {
      onVacantClick({
        roomId: bed.roomId,
        roomNumber: bed.roomNumber,
        bedId: bed.bedId,
        bedNumber: bed.bedNumber,
        floor: bed.floor,
        roomType: bed.roomType
      });
    }
  };

  const handleOccupiedClick = (bed) => {
    if (bed.OccupancyStatus === 'OCCUPIED' && bed.AdmissionID) {
      setSelectedBedForCheckout(bed);
      setIsCheckoutModalOpen(true);
    }
  };

  // Handle successful checkout
  const handleCheckoutSuccess = (bedId) => {
    // Refresh the occupancy data
    fetchOccupancyData();
    // You can also show a success message here
    console.log(`Bed ${bedId} checkout successful`);
  };

  // Filter rooms based on floor and selected statuses
  const filtered = roomsData.filter(room => {
    // Floor filter - show all if 'all' selected, otherwise match specific floor
    const matchFloor = floorFilter === 'all' || room.floor === floorFilter

    // Search filter
    const matchSearch = !search ||
      String(room.id).toLowerCase().includes(search.toLowerCase()) ||
      room.beds.some(bed => bed.name && bed.name.toLowerCase().includes(search.toLowerCase()))

    // Status filter - if no status selected, show all rooms
    let matchStatus = true
    if (selectedStatuses.length > 0) {
      // Room should have at least one bed with any of the selected statuses
      matchStatus = room.beds.some(bed => selectedStatuses.includes(bed.status))
    }

    return matchFloor && matchSearch && matchStatus
  })

  const availableFloors = getAvailableFloors()

  if (roomsData.length === 0) {
    return (
      <div className="occ-root">
        <div className="occ-empty">No occupancy data available for {selectedPG}</div>
      </div>
    )
  }
  
  if (loading) {
    return (
      <div className="occ-root">
        <div className="occ-loading">Loading occupancy data...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="occ-root">
        <div className="occ-error">Error: {error}</div>
      </div>
    )
  }

  return (
    <div className="occ-root">

      {/* ── Summary Stats ── */}
      <div className="occ-summary">
        <div className="occ-legend">
          {Object.entries(STATUS).map(([k, v]) => (
            <span key={k} className="occ-legend-item">
              <span className="occ-dot" style={{ background: v.color }} />{v.label}
            </span>
          ))}
        </div>
        <div className="occ-stats">
          <span>Rooms: <strong>{stats.totalRooms}</strong></span>
          <span className="occ-stat-sep" />
          <span>Beds: <strong>{stats.totalBeds}</strong></span>
          <span className="occ-stat-sep" />
          <span>Occupied: <strong className="occ-green">{stats.occupiedBeds}</strong></span>
          <span className="occ-stat-sep" />
          <span>Notice: <strong className="occ-amber">{stats.noticeBeds}</strong></span>
          <span className="occ-stat-sep" />
          <span>Vacant: <strong className="occ-red">{stats.vacantBeds}</strong></span>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="occ-controls">
        <div className="occ-search-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            className="occ-search"
            placeholder="Search room or tenant…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Floor pills - First: All, Ground Floor, 1st Floor, 2nd Floor, etc. */}
        <div className="occ-filter-group">
          <button
            className={`occ-floor-pill ${floorFilter === 'all' ? 'active' : ''}`}
            onClick={() => setFloorFilter('all')}
          >
            All
          </button>
          {availableFloors.map(floor => (
            <button
              key={floor}
              className={`occ-floor-pill ${floorFilter === floor ? 'active' : ''}`}
              onClick={() => setFloorFilter(floor)}
            >
              {floor}
            </button>
          ))}
        </div>

        {/* Status filter pills - Multi-select (Occupied, Notice Period, Vacant) - UPDATED to match revenue style */}
        <div className="occ-filter-group">
          {STATUS_FILTERS.map(f => {
            const isSelected = selectedStatuses.includes(f.key)
            
            return (
              <button 
                key={f.key}
                className="occ-status-pill"
                style={{
                  background: isSelected ? f.bg : '#F3F4F6',
                  color: isSelected ? 'white' : '#4B5563',
                  borderColor: isSelected ? f.bg : '#E5E7EB',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => toggleStatusFilter(f.key)}>
                {f.label}
                {isSelected && (
                  <span className="occ-checkmark"> ✓</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Room Grid ── */}
      <div className="occ-grid">
        {filtered.map(room => (
          <div key={room.id} className="occ-card">
            <div className="occ-card-head">
              <span className="occ-card-room">Room {room.id}</span>
              <div className="occ-card-badges">
                <span className="occ-card-floor">{room.floor}</span>
                <span className="occ-card-type">{room.roomType}</span>
              </div>
            </div>

            <div className="occ-pill-grid">
              {room.beds.map((bed, i) => {
                const bedStatus = bed.status.toUpperCase()
                const cfg = STATUS[bedStatus] || STATUS.VACANT
                const isVacant = bed.status === 'vacant'
                const isOccupied = bed.status === 'occupied'

                return (
                  <div
                    key={i}
                    className={`occ-pill ${isVacant || isOccupied ? 'occ-pill-clickable' : ''}`}
                    style={{
                      background: cfg.bg,
                      borderColor: cfg.border,
                      cursor: isVacant || isOccupied ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (isVacant && onVacantClick) {
                        handleVacantClick(bed);
                      }
                      if (isOccupied && bed.OccupancyStatus === 'OCCUPIED') {
                        handleOccupiedClick(bed);
                      }
                    }}
                  >
                    <span className="occ-dot" style={{ background: cfg.color }} />
                    <span className="occ-pill-name" style={{ color: cfg.text }}>
                      {bed.name || cfg.label}
                    </span>
                  </div>
                )
              })}
              {/* Add empty placeholders to maintain grid alignment */}
              {room.beds.length < 4 && [...Array(4 - room.beds.length)].map((_, i) => (
                <div key={`empty-${i}`} className="occ-pill occ-pill-ghost" />
              ))}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="occ-no-results">
          No rooms match your search criteria
        </div>
      )}

      {/* Tenant Checkout Modal */}
      {isCheckoutModalOpen && selectedBedForCheckout && (
        <TenantCheckoutModal
          bed={selectedBedForCheckout}
          pgId={selectedPGId}
          onClose={() => {
            setIsCheckoutModalOpen(false);
            setSelectedBedForCheckout(null);
          }}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </div>
  )
}

export default OwnerOccupancy